import { IAgenda, IAppointment, IDepartment } from '../constants/types';
import { RequestStatus } from '../pages/hospital/Request/types';

const EXCLUDED_PREFILL_STATUSES = new Set<number>([
    RequestStatus.CANCELED,
    RequestStatus.CANCELED_BY_USER,
    RequestStatus.EXPIRED,
]);

export type AppointmentPrefillWarning = 'agenda_unavailable' | 'service_unavailable' | 'partial';

export interface AppointmentFormPrefill {
    departmentUuid: string | null;
    agendaUuid: string | null;
    serviceId: number | null;
    warnings: AppointmentPrefillWarning[];
}

type ServiceInvestigationRow = {
    id: number;
    description?: string;
};

function getRequestServiceInvestigation(appointment: IAppointment): {
    serviceInvestigationId: number | null;
    description: string;
} {
    const row = (appointment.requestAppointment as { requestsServiceInvestigation?: Array<{
        id?: number;
        serviceInvestigation?: { id?: number; description?: string };
    }> })?.requestsServiceInvestigation?.[0];

    const serviceInvestigationId = row?.serviceInvestigation?.id ?? null;
    const description = row?.serviceInvestigation?.description ?? '';
    return { serviceInvestigationId, description };
}

export function getLastValidConsultationAppointment(appointments: IAppointment[]): IAppointment | null {
    return appointments
        .filter((appointment) => !EXCLUDED_PREFILL_STATUSES.has(appointment.requestAppointment.status))
        .sort((a, b) => new Date(b.startDateTime).getTime() - new Date(a.startDateTime).getTime())[0] ?? null;
}

/** Copy template visit type; première consultation (0) becomes follow-up (1). */
export function resolvePrefillVisitType(templateType: number): number {
    return templateType === 0 ? 1 : templateType;
}

function normalizeDescription(description: string): string {
    return description.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

function isPremiereServiceDescription(description: string): boolean {
    const normalized = normalizeDescription(description);
    return normalized.includes('premiere visite') || normalized.includes('first visit');
}

function isFollowUpServiceDescription(description: string): boolean {
    const normalized = normalizeDescription(description);
    return normalized.includes('controle') || normalized.includes('suivi') || normalized.includes('follow-up') || normalized.includes('follow up');
}

function findFollowUpService(agenda: IAgenda, templateDescription: string): ServiceInvestigationRow | null {
    const services = (agenda.listServicesInvestigation ?? []) as ServiceInvestigationRow[];
    const templateBase = templateDescription.split('(')[0]?.trim().toLowerCase() ?? '';

    const pairedFollowUp = services.find((service) => {
        const description = service.description ?? '';
        if (!isFollowUpServiceDescription(description)) {
            return false;
        }
        if (!templateBase) {
            return true;
        }
        return normalizeDescription(description).includes(normalizeDescription(templateBase));
    });
    if (pairedFollowUp) {
        return pairedFollowUp;
    }

    return services.find((service) => isFollowUpServiceDescription(service.description ?? '')) ?? null;
}

function resolveServiceForPrefill(
    agenda: IAgenda,
    template: IAppointment,
    targetVisitType: number,
): { serviceId: number | null; serviceUnavailable: boolean } {
    const services = (agenda.listServicesInvestigation ?? []) as ServiceInvestigationRow[];
    const { serviceInvestigationId, description: templateDescription } = getRequestServiceInvestigation(template);
    const templateService = serviceInvestigationId
        ? services.find((service) => service.id === serviceInvestigationId)
        : null;

    if (targetVisitType === 1 && template.type === 0) {
        const followUpService = findFollowUpService(
            agenda,
            templateDescription || templateService?.description || '',
        );
        if (followUpService) {
            return { serviceId: followUpService.id, serviceUnavailable: false };
        }
        if (templateService && isPremiereServiceDescription(templateService.description ?? '')) {
            return { serviceId: null, serviceUnavailable: true };
        }
    }

    if (templateService) {
        return { serviceId: templateService.id, serviceUnavailable: false };
    }

    if (serviceInvestigationId && !templateService) {
        return { serviceId: null, serviceUnavailable: true };
    }

    if (services.length === 1) {
        return { serviceId: services[0].id, serviceUnavailable: false };
    }

    return { serviceId: null, serviceUnavailable: false };
}

/** SMS-enabled investigations pass the patient phone to appointment creation. */
export function resolveAppointmentPhone(
    outpatientsInfo: { sms?: boolean } | null | undefined,
    patientPhone: string | null | undefined,
): string {
    if (!outpatientsInfo?.sms) {
        return '';
    }
    return patientPhone ?? '';
}

export function resolveListAgendasForDepartment(
    agendas: IAgenda[],
    departmentsWithAgenda: IDepartment[],
    department: IDepartment | null,
    showAllAgendas: boolean,
): IAgenda[] {
    if (departmentsWithAgenda.length === 0) {
        return agendas.filter((agenda) => agenda.uuidDepartment === null);
    }
    if (!showAllAgendas && department) {
        return agendas.filter((agenda) =>
            (agenda.department as IDepartment | undefined)?.uuid === department.uuid
            || agenda.uuidDepartment === department.uuid);
    }
    return agendas;
}

export interface AppointmentPrefillApplication {
    warnings: AppointmentPrefillWarning[];
    department: IDepartment | null;
    agenda: IAgenda | null;
    service: { id: number } | null;
}

export function applyAppointmentFormPrefill(
    template: IAppointment,
    agendas: IAgenda[],
    departmentsWithAgenda: IDepartment[],
): AppointmentPrefillApplication {
    const prefill = buildAppointmentFormPrefill(template, agendas);
    let department: IDepartment | null = null;

    if (prefill.departmentUuid) {
        department = departmentsWithAgenda.find((dept) => dept.uuid === prefill.departmentUuid)
            ?? agendas.find((agenda) => agenda.department?.uuid === prefill.departmentUuid)?.department
            ?? null;
    }

    const agendasForDepartment = resolveListAgendasForDepartment(
        prefill.departmentUuid
            ? agendas.filter((agenda) =>
                (agenda.department as IDepartment | undefined)?.uuid === prefill.departmentUuid
                || agenda.uuidDepartment === prefill.departmentUuid)
            : agendas,
        departmentsWithAgenda,
        department,
        false,
    );

    let agenda: IAgenda | null = null;
    if (prefill.agendaUuid) {
        agenda = agendasForDepartment.find((candidate) => candidate.uuid === prefill.agendaUuid) ?? null;
    }

    let service: { id: number } | null = null;
    if (prefill.serviceId) {
        const agendaForService = agenda ?? agendasForDepartment[0];
        const matchingService = agendaForService?.listServicesInvestigation.find(
            (candidate) => candidate.id === prefill.serviceId,
        );
        if (matchingService) {
            service = matchingService;
        }
    }

    return {
        warnings: prefill.warnings,
        department,
        agenda,
        service,
    };
}

export function buildAppointmentFormPrefill(
    template: IAppointment | null,
    agendas: IAgenda[],
): AppointmentFormPrefill {
    if (!template) {
        return { departmentUuid: null, agendaUuid: null, serviceId: null, warnings: [] };
    }

    const warnings: AppointmentPrefillWarning[] = [];
    const departmentUuid = template.agenda?.department?.uuid ?? template.agenda?.uuidDepartment ?? null;
    const agenda = agendas.find((candidate) => candidate.uuid === template.agenda?.uuid) ?? null;

    if (!agenda) {
        if (departmentUuid) {
            warnings.push('agenda_unavailable', 'partial');
        } else {
            warnings.push('agenda_unavailable');
        }
        return { departmentUuid, agendaUuid: null, serviceId: null, warnings };
    }

    const targetVisitType = resolvePrefillVisitType(template.type);
    const { serviceId, serviceUnavailable } = resolveServiceForPrefill(agenda, template, targetVisitType);

    if (serviceUnavailable) {
        warnings.push('service_unavailable', 'partial');
    }

    return {
        departmentUuid,
        agendaUuid: agenda.uuid,
        serviceId,
        warnings,
    };
}
